import { memo, type CSSProperties } from 'react';
import clsx from 'clsx';
import { Group, Stack, Text } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { formatCoordinates } from '@/utils/helpers';
import { FONT_WEIGHT_SEMIBOLD, ICON_SIZE_XS } from '@/constants/ui';
import type { Note } from '@/types/notes';
import styles from './DetailHeader.module.css';

interface DetailHeaderProps {
  note: Note;
  className?: string;
}

export const DetailHeader = memo(function DetailHeader({
  note,
  className,
}: DetailHeaderProps) {
  const { t } = useTranslation();

  const { latitude, longitude } = note;
  const hasLocation =
    latitude !== null &&
    latitude !== undefined &&
    longitude !== null &&
    longitude !== undefined;

  return (
    <Stack gap="2xs" className={clsx(styles.titleStack, className)}>
      <div className={styles.titleRow}>
        <div
          className={styles.colorDot}
          style={{ '--note-color': note.color } as CSSProperties}
        />
        <Text
          size="lg"
          fw={FONT_WEIGHT_SEMIBOLD}
          lineClamp={2}
          className={styles.title}
        >
          {note.title}
        </Text>
      </div>

      {hasLocation && (
        <Group gap="2xs" className={styles.locationGroup}>
          <IconMapPin size={ICON_SIZE_XS} className={styles.locationIcon} />
          {note.location ? (
            <>
              <Text size="xs" c="dimmed" className={styles.roadName}>
                {note.location}
              </Text>
              <Text
                size="xs"
                c="dimmed"
                ff="monospace"
                className={styles.coordinates}
              >
                {formatCoordinates(latitude, longitude)}
              </Text>
            </>
          ) : (
            <Text
              size="xs"
              c="dimmed"
              ff="monospace"
              className={styles.coordinates}
            >
              {t('notes.detail.pinnedLocation')}{' '}
              {formatCoordinates(latitude, longitude)}
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );
});
